import { useAbility } from "@casl/react";
import { type FC, type FormEvent, type ReactNode, useState } from "react";

import type { AppAbility } from "@recobra/core";
import { m } from "@recobra/i18n/messages";
import type { Task } from "@recobra/schemas/task";
import { Alert, AlertDescription } from "@recobra/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@recobra/ui/alert-dialog";
import { Button } from "@recobra/ui/button";
import { DialogFooter } from "@recobra/ui/dialog";

import type { TaskProgressInput } from "../task-data";
import { TaskContextStatus } from "../task-details";
import { TaskDetailsSummary } from "../task-details";
import type { TaskMetadataEditor } from "../task-details";
import type { TaskRequirementContext } from "../task-details";
import { TaskCompletionButton } from "./TaskCompletionButton";
import { TaskExecutionFields } from "./TaskExecutionFields";
import {
  type TaskCompletionInput,
  type TaskExecutionPolicy,
  useTaskExecutionDraft,
} from "./useTaskExecutionDraft";

/**
 * Everything needed to finalize one task: what it is, the controls, the
 * completion gate, and the confirm/cancel pair.
 */
export const TaskExecutionSection: FC<{
  task: Task;
  leadingAction?: ReactNode;
  context: TaskRequirementContext;
  policy: TaskExecutionPolicy;
  editor: TaskMetadataEditor;
  onProgress: (input: TaskProgressInput) => void;
  onCancel: () => void;
  onConfirm: (input: TaskCompletionInput) => void;
  isSubmitting: boolean;
}> = ({
  task,
  leadingAction,
  context,
  policy,
  editor,
  onProgress,
  onCancel,
  onConfirm,
  isSubmitting,
}) => {
  const ability = useAbility<AppAbility>();
  const canWaive =
    task.system === "provide_case_documents" || ability.can("waiveRequirements", "Task");
  const draft = useTaskExecutionDraft({ task, policy, context });
  const canComplete = draft.canSubmit && !isSubmitting && context.taskType !== undefined;
  const [pendingCompletion, setPendingCompletion] = useState<TaskCompletionInput | null>(null);
  const formId = `task-execution-${task.id}`;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canComplete) return;

    if (task.reminders.some((reminder) => reminder.status === "pending")) {
      setPendingCompletion(draft.completionInput);
      return;
    }
    draft.cancelNotesSave();
    onConfirm(draft.completionInput);
  };
  return (
    <>
      <div className="-mx-4 max-h-(--task-content-max-height) overflow-y-auto px-4 py-1">
        <form id={formId} onSubmit={submit} className="flex flex-col divide-y">
          <TaskDetailsSummary task={task} editor={editor} />
          <TaskContextStatus context={context} />
          {context.unconfigured ? (
            <Alert>
              <AlertDescription>{m.api_error_task_type_unconfigured()}</AlertDescription>
            </Alert>
          ) : null}
          <TaskExecutionFields
            task={task}
            draft={draft}
            context={context}
            policy={policy}
            editor={editor}
            canWaive={canWaive}
            onSatisfy={(requirementKey, satisfied) =>
              onProgress({ satisfyRequirements: { [requirementKey]: satisfied } })
            }
            onWaive={(requirementKey, waived) =>
              onProgress({ waiveRequirements: { [requirementKey]: waived } })
            }
            isSubmitting={isSubmitting}
            onNavigate={onCancel}
          />
        </form>
      </div>

      <DialogFooter className="sm:justify-start">
        {leadingAction}
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {m.dialog_close()}
        </Button>
        <TaskCompletionButton
          form={formId}
          disabled={!canComplete}
          disabledReason={!isSubmitting ? draft.blockedReason : undefined}
        />
      </DialogFooter>

      <AlertDialog
        open={pendingCompletion !== null}
        onOpenChange={(open) => !open && !isSubmitting && setPendingCompletion(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.task_reminders_complete_confirm_title()}</AlertDialogTitle>
            <AlertDialogDescription>
              {m.task_reminders_complete_confirm_description()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>{m.task_cancel()}</AlertDialogCancel>
            <AlertDialogAction
              disabled={!canComplete}
              onClick={() => {
                if (!pendingCompletion || !canComplete) return;
                draft.cancelNotesSave();
                onConfirm(pendingCompletion);
                setPendingCompletion(null);
              }}
            >
              {m.task_reminders_complete_confirm_action()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
